import Cookies from 'js-cookie';

import { integrationsToDisplayName } from '../tracking/integrations';
import { integrations } from '../tracking/integrations';
import { getTrackingService } from '../tracking/TrackingServiceInstance';
import trackingV3 from '../tracking/v3.exec';
import trackingV4 from '../tracking/v4.calls';

// The server-side CAPI (`facebook-api`) event is built from this payload
// downstream in lemon-worker, keyed on these fields. If another part of the app
// changes the tracking event shape, the middleware would keep forwarding events
// that silently lose user/event attribution in Meta (this is how the missing
// `event_source_url` in CAP-4114 went unnoticed). `event` is guaranteed by the
// guard below, and page url / user agent are backfilled, so we check the
// remaining identifiers here purely for observability.
const CAPI_REQUIRED_FIELDS = ['messageId', 'anonymousId'] as const;

const warnOnMissingCapiFields = (nested: Record<string, unknown>) => {
    const missing = CAPI_REQUIRED_FIELDS.filter((field) => nested[field] == null);

    if (missing.length > 0) {
        console.warn('facebook-api: CAPI payload is missing expected fields', {
            data: { event: nested.event, missing },
        });
    }
};

const middleware = (payload: any, integrationName: string, next: (payload: any) => void) => {
    if (integrationName !== integrationsToDisplayName[integrations.facebookPixel]) {
        return next(payload);
    }

    // Defensive: a malformed payload throwing here would break the whole Segment
    // integration-middleware chain and, with it, client-side pixel tracking.
    if (!payload?.obj?.event) {
        return next(payload);
    }

    const source = JSON.parse(JSON.stringify(payload.obj));

    // CAP-4114: Meta blocks CAPI events for restricted-category pixels (e.g. Health)
    // when `event_source_url` is missing. lemon-worker derives it from
    // `nested.context.page.url` (and the client user agent from
    // `nested.context.userAgent`), so set both explicitly here instead of relying on
    // whatever Segment happened to attach to the intercepted Pixel event — the v4
    // pipeline's event context (v4.calls.ts generateContext) has no `page` at all.
    const nested = {
        ...source,
        context: {
            ...source.context,
            page: {
                ...source.context?.page,
                url: source.context?.page?.url ?? window.location.href,
            },
            userAgent: source.context?.userAgent ?? window.navigator.userAgent,
        },
        fbp: Cookies.get('_fbp'),
        fbc: Cookies.get('_fbc'),
        userTraits: window.analytics?.user?.()?.traits() ?? {},
    };

    warnOnMissingCapiFields(nested);

    const facebookApiEventName = 'facebook-api';

    const service = getTrackingService();

    if (service.isV3()) {
        const data = {
            // need to set globals here because tracking v3 will scan through properties for
            // perspective data like campaignId, companyId, etc.
            ...service.getEventContext(),
            nested,
        };
        trackingV3.track(facebookApiEventName, data);
    } else if (service.isV4()) {
        const data = { nested };
        // context is set in the .track() call which used by tracking-v4 data-pipeline
        trackingV4.track(facebookApiEventName, data, {
            integrations: {
                All: false,
                Perspective: true,
            },
        });
    }
    return next(payload);
};

export default middleware;
